import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import {
    Shield,
    Plus,
    Search,
    Filter,
    ChevronRight,
    Calendar,
    DollarSign,
    Percent,
    Users,
    Building,
    Clock,
    AlertCircle,
    CheckCircle,
    TrendingUp,
    BarChart3,
    UserCheck,
    Eye,
    FileText,
    Anchor,
    Globe,
    MapPin,
    Loader2
} from 'lucide-react';
import api from '../../services/api';

export default function OpenCoverList() {
    const { user } = useSelector((state) => state.auth);
    const [filteredCovers, setFilteredCovers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');

    const { data: covers, isLoading, error, refetch } = useQuery('open-covers', () =>
        api.get('/quotes/open-covers').then((res) => res.data)
    );

    // Filter covers based on user role and search
    useEffect(() => {
        if (!covers) return;

        let result = [...covers];

        // Role-based filtering
        if (user?.role === 'admin' || user?.role === 'underwriter') {
            // See all covers
        } else if (user?.role === 'agent' || user?.role === 'broker') {
            result = result.filter(cover =>
                cover.created_by === user.id ||
                cover.agent_id === user.id ||
                cover.broker_id === user.id
            );
        } else if (user?.role === 'direct_client') {
            result = result.filter(cover =>
                cover.client_id === user.id
            );
        }

        // Search filter
        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            result = result.filter(cover =>
                cover.cover_number?.toLowerCase().includes(search) ||
                cover.client_name?.toLowerCase().includes(search) ||
                cover.created_by_name?.toLowerCase().includes(search) ||
                cover.broker_name?.toLowerCase().includes(search)
            );
        }

        // Status filter
        const now = new Date();
        if (statusFilter === 'active') {
            result = result.filter(cover => new Date(cover.end_date) > now);
        } else if (statusFilter === 'expiring') {
            const twoWeeksFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
            result = result.filter(cover =>
                new Date(cover.end_date) > now &&
                new Date(cover.end_date) <= twoWeeksFromNow
            );
        } else if (statusFilter === 'expired') {
            result = result.filter(cover => new Date(cover.end_date) <= now);
        }

        // Sort
        switch (sortBy) {
            case 'newest':
                result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case 'oldest':
                result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                break;
            case 'limit_high':
                result.sort((a, b) => Number(b.sum_insured_limit) - Number(a.sum_insured_limit));
                break;
            case 'limit_low':
                result.sort((a, b) => Number(a.sum_insured_limit) - Number(b.sum_insured_limit));
                break;
            case 'usage_high':
                result.sort((a, b) => {
                    const usageA = (Number(a.used_amount || 0) / Number(a.sum_insured_limit)) * 100;
                    const usageB = (Number(b.used_amount || 0) / Number(b.sum_insured_limit)) * 100;
                    return usageB - usageA;
                });
                break;
            default:
                break;
        }

        setFilteredCovers(result);
    }, [covers, user, searchTerm, statusFilter, sortBy]);

    // Calculate stats
    const stats = {
        total: filteredCovers?.length || 0,
        active: filteredCovers?.filter(c => new Date(c.end_date) > new Date()).length || 0,
        expiring: filteredCovers?.filter(c => {
            const now = new Date();
            const twoWeeksFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
            return new Date(c.end_date) > now && new Date(c.end_date) <= twoWeeksFromNow;
        }).length || 0,
        expired: filteredCovers?.filter(c => new Date(c.end_date) <= new Date()).length || 0,
        totalLimit: filteredCovers?.reduce((sum, c) => sum + Number(c.sum_insured_limit), 0) || 0,
        totalUsed: filteredCovers?.reduce((sum, c) => sum + Number(c.used_amount || 0), 0) || 0,
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                    <div className="animate-pulse">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-48" />
                                <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-64 mt-2" />
                            </div>
                            <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-xl w-32" />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="h-24 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                            ))}
                        </div>
                        <div className="h-12 bg-gray-100 dark:bg-slate-700 rounded-xl mt-6" />
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-40 bg-gray-100 dark:bg-slate-700 rounded-2xl mt-4" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
                <div className="text-center max-w-md p-8">
                    <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Failed to Load Open Covers</h3>
                    <p className="text-gray-500 dark:text-slate-400">{error.message || 'An error occurred while loading open covers.'}</p>
                    <button
                        onClick={() => refetch()}
                        className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#3a663f] text-white rounded-lg font-medium hover:bg-[#2d5231] transition-colors"
                    >
                        <Loader2 className="w-4 h-4" />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    //==const canCreate = user?.role === 'agent' || user?.role === 'broker' || user?.role === 'admin';

    const CREATE_ALLOWED_ROLES = ['agent', 'broker', 'admin', 'underwriter'];
    const canCreate = CREATE_ALLOWED_ROLES.includes(user?.role);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#3a663f]/10 dark:bg-[#3a663f]/20 rounded-lg">
                                <Shield className="w-6 h-6 text-[#3a663f] dark:text-green-400" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-extrabold text-gray-800 dark:text-white">Open Covers</h1>
                                <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">
                                    {filteredCovers?.length || 0} active open cover{filteredCovers?.length !== 1 ? 's' : ''}
                                    {covers && covers.length !== filteredCovers?.length && (
                                        <span className="ml-2 text-xs text-gray-400">
                                            (Showing {filteredCovers.length} of {covers.length} total)
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                    {canCreate && (
                        <Link
                            to="/open-covers/new"
                            className="inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5231] text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            <Plus className="w-5 h-5" />
                            New Open Cover
                        </Link>
                    )}
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <StatCard
                        label="Total"
                        value={stats.total}
                        icon={BarChart3}
                        color="bg-[#3a663f]"
                    />
                    <StatCard
                        label="Active"
                        value={stats.active}
                        icon={CheckCircle}
                        color="bg-green-500"
                    />
                    <StatCard
                        label="Expiring Soon"
                        value={stats.expiring}
                        icon={Clock}
                        color="bg-amber-500"
                    />
                    <StatCard
                        label="Expired"
                        value={stats.expired}
                        icon={AlertCircle}
                        color="bg-red-500"
                    />
                    <StatCard
                        label="Total Sum insured Limit"
                        value={`KES ${stats.totalLimit.toLocaleString()}`}
                        icon={DollarSign}
                        color="bg-blue-500"
                    />
                </div>

                {/* Filter and Search Bar */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Search */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search by cover number, client, or broker..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a663f] focus:border-transparent transition-all"
                            />
                        </div>

                        {/* Status Filter */}
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a663f] focus:border-transparent transition-all"
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="expiring">Expiring Soon</option>
                            <option value="expired">Expired</option>
                        </select>

                        {/* Sort */}
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a663f] focus:border-transparent transition-all"
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="limit_high">Highest Limit</option>
                            <option value="limit_low">Lowest Limit</option>
                            <option value="usage_high">Highest Usage</option>
                        </select>

                        <button
                            onClick={() => refetch()}
                            className="p-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-green-400 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                        >
                            <Loader2 className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Empty State */}
                {(!filteredCovers || filteredCovers.length === 0) && (
                    <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700">
                        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gray-100 dark:bg-slate-700 mb-6">
                            <Shield className="w-12 h-12 text-gray-300 dark:text-slate-500" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                            {searchTerm ? 'No matching open covers' :
                                covers && covers.length > 0 && filteredCovers.length === 0
                                    ? 'You have no open covers'
                                    : 'No open covers yet'}
                        </h3>
                        <p className="text-gray-500 dark:text-slate-400 max-w-sm mx-auto mb-6">
                            {searchTerm
                                ? `No open covers match your search for "${searchTerm}"`
                                : covers && covers.length > 0 && filteredCovers.length === 0
                                    ? "You don't have any open covers created. Create one to start issuing quotes with predefined terms."
                                    : 'Create an open cover agreement to issue multiple cargo quotes with predefined terms.'}
                        </p>
                        {canCreate && !searchTerm && (
                            <Link
                                to="/open-covers/new"
                                className="inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5231] text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl"
                            >
                                <Plus className="w-5 h-5" />
                                Create Open Cover
                            </Link>
                        )}
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="text-[#3a663f] dark:text-green-400 font-medium hover:underline"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                )}

                {/* Open Covers List */}
                {filteredCovers && filteredCovers.length > 0 && (
                    <div className="space-y-4">
                        {filteredCovers.map((cover) => {
                            const usedPercent = (Number(cover.used_amount || 0) / Number(cover.sum_insured_limit)) * 100;
                            const remaining = Number(cover.sum_insured_limit) - Number(cover.used_amount || 0);
                            const now = new Date();
                            const endDate = new Date(cover.end_date);
                            const isExpired = endDate <= now;
                            const isExpiringSoon = !isExpired && endDate < new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
                            const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

                            return (
                                <div
                                    key={cover.id}
                                    className={`bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border transition-all hover:shadow-lg hover:border-[#3a663f]/30 dark:hover:border-green-400/30 ${isExpired
                                            ? 'border-red-200 dark:border-red-800/50'
                                            : isExpiringSoon
                                                ? 'border-amber-200 dark:border-amber-800/50'
                                                : 'border-gray-200 dark:border-slate-700'
                                        }`}
                                >
                                    <div className="flex flex-col lg:flex-row justify-between gap-6">
                                        {/* Left: Cover Info */}
                                        <div className="flex-1 space-y-4">
                                            {/* Header Row */}
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <div className="flex items-center gap-2">
                                                    <Shield className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                                    <h3 className="font-bold text-gray-800 dark:text-white text-lg">
                                                        {cover.cover_number}
                                                    </h3>
                                                </div>
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${isExpired
                                                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                                        : isExpiringSoon
                                                            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                                                            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                    }`}>
                                                    {isExpired ? (
                                                        <>
                                                            <AlertCircle className="w-3 h-3" />
                                                            Expired
                                                        </>
                                                    ) : isExpiringSoon ? (
                                                        <>
                                                            <Clock className="w-3 h-3" />
                                                            {daysRemaining} days left
                                                        </>
                                                    ) : (
                                                        <>
                                                            <CheckCircle className="w-3 h-3" />
                                                            Active
                                                        </>
                                                    )}
                                                </span>
                                                {(user?.role === 'admin' || user?.role === 'underwriter') && cover.created_by_name && (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium rounded-full">
                                                        <UserCheck className="w-3 h-3" />
                                                        {cover.created_by_name}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Details Grid */}
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                <DetailItem
                                                    icon={Users}
                                                    label="Client"
                                                    value={cover.client_name}
                                                />
                                                <DetailItem
                                                    icon={DollarSign}
                                                    label="Total Limit"
                                                    value={`${cover.currency} ${Number(cover.sum_insured_limit).toLocaleString()}`}
                                                />
                                                <DetailItem
                                                    icon={Percent}
                                                    label="Marine / War Rate"
                                                    value={`${cover.marine_rate}% / ${cover.war_rate}%`}
                                                />
                                                <DetailItem
                                                    icon={Calendar}
                                                    label="Valid Period"
                                                    value={`${new Date(cover.start_date).toLocaleDateString()} - ${new Date(cover.end_date).toLocaleDateString()}`}
                                                />
                                                <DetailItem
                                                    icon={FileText}
                                                    label="Quotes Issued"
                                                    value={cover.quote_count || 0}
                                                />
                                                <DetailItem
                                                    icon={DollarSign}
                                                    label="Used Amount"
                                                    value={`${cover.currency} ${Number(cover.used_amount || 0).toLocaleString()}`}
                                                />
                                                <DetailItem
                                                    icon={DollarSign}
                                                    label="Remaining"
                                                    value={`${cover.currency} ${remaining.toLocaleString()}`}
                                                    valueClassName={remaining <= 0 ? 'text-red-600' : 'text-green-600'}
                                                />
                                                {cover.broker_name && (
                                                    <DetailItem
                                                        icon={Building}
                                                        label="Broker"
                                                        value={cover.broker_name}
                                                    />
                                                )}
                                            </div>

                                            {/* Terms & Conditions */}
                                            {cover.terms_conditions && (
                                                <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
                                                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                                                        <FileText className="w-3 h-3" />
                                                        Terms & Conditions
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-slate-300 line-clamp-2">
                                                        {cover.terms_conditions}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Right: Usage & Actions */}
                                        <div className="lg:w-64 flex-shrink-0 space-y-4">
                                            {/* Usage Bar */}
                                            <div>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-gray-400">Usage</span>
                                                    <span className={`font-medium ${usedPercent > 80 ? 'text-red-600' :
                                                            usedPercent > 50 ? 'text-amber-600' :
                                                                'text-green-600'
                                                        }`}>
                                                        {usedPercent.toFixed(1)}%
                                                    </span>
                                                </div>
                                                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-500 ${usedPercent > 80 ? 'bg-red-500' :
                                                                usedPercent > 50 ? 'bg-amber-500' :
                                                                    'bg-[#3a663f]'
                                                            }`}
                                                        style={{ width: `${Math.min(usedPercent, 100)}%` }}
                                                    />
                                                </div>
                                                <div className="flex justify-between text-xs mt-1">
                                                    <span className="text-gray-400">Used: {cover.currency} {Number(cover.used_amount || 0).toLocaleString()}</span>
                                                    <span className="text-gray-400">Remaining: {cover.currency} {remaining.toLocaleString()}</span>
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex gap-2">
                                                <Link
                                                    to={`/open-covers/${cover.id}`}
                                                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#3a663f] hover:bg-[#2d5231] text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg text-sm"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    View Details
                                                </Link>
                                                
                                            </div>

                                            {/* Expiry Date */}
                                            <div className={`text-xs text-center ${isExpired ? 'text-red-500' :
                                                    isExpiringSoon ? 'text-amber-500' :
                                                        'text-gray-400'
                                                }`}>
                                                {isExpired ? (
                                                    <span className="flex items-center justify-center gap-1">
                                                        <AlertCircle className="w-3 h-3" />
                                                        Expired on {new Date(cover.end_date).toLocaleDateString()}
                                                    </span>
                                                ) : isExpiringSoon ? (
                                                    <span className="flex items-center justify-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        Expires in {daysRemaining} days ({new Date(cover.end_date).toLocaleDateString()})
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center justify-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        Valid until {new Date(cover.end_date).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

// ========== SUB-COMPONENTS ==========

function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider">{label}</p>
                    <p className="text-xl font-bold text-gray-800 dark:text-white mt-0.5 truncate">{value}</p>
                </div>
                <div className={`p-2 rounded-lg ${color} text-white`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
        </div>
    );
}

function DetailItem({ icon: Icon, label, value, valueClassName = '' }) {
    return (
        <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <Icon className="w-3 h-3" />
                {label}
            </p>
            <p className={`font-medium text-gray-700 dark:text-slate-300 ${valueClassName}`}>
                {value || '-'}
            </p>
        </div>
    );
}